import { buildKeyToEvent, collectLeafStrings } from '@core/services/gtm/gtm.utils';

export type ScenarioBlindTypes = 'outdoor' | 'interior' | 'both';
export type BgTypes = 'project_sample' | 'upload' | 'live' | 'ar' | 'no_background';
export type SBMContext = 'welcome' | 'select_opening_screen' | 'control_panel' | 'view_switcher';
export type DialogCloseSource = 'x_button' | 'cancel_button' | 'unknown' | 'backdrop' | 'escape' | 'programmatic';
export type QuizCloseMethod =
	| 'view_change'
	| 'to_main_screen'
	| 'to_sos'
	| 'to_overview'
	| 'new_background'
	| 'restart_detection'
	| 'escape'
	| 'x_button'
	| 'close_button'
	| 'unknown';
export type AnyKey = keyof typeof EVENT_KEY_MAP;

/*
+===================================================================================+
|					 	 DYB-Analytics Event Schemas						    	|
+-----------------------------------------------------------------------------------+
|	The interfaces declared below – and gathered into the 'GtmEvent' union – are	|
|	the payload shapes DYB can send through pushEvent() to Google Tag Manager.		|
|	For each concrete interface in GtmEvent, the literal 'event' property value		|
|	becomes the event name that will surface in GA4 reports. In other words,		|
|	every interface included in GtmEvent maps 1-to-1 to a GA4 event.				|
+===================================================================================+
*/

export interface CoreParams {
	event: string;
	event_key: string;
	session_id: string;
	user_session_num: number;
	product_mode: 'dyb' | 'shop';
}

interface SessionStart extends CoreParams {
	event: 'zt_session_start';
	visitor_id: string; // should make this a CoreParam?
	embedded: boolean;
	app_version?: string;
}

interface SessionReturn extends CoreParams {
	event: 'zt_session_return';
	visitor_id: string; // should make this a CoreParam?
	session_return_num: number; // Should this be propagated??
	app_version?: string;
	// time between return?
	// return method? only really email
}

interface SessionHiatus extends CoreParams {
	event: 'zt_session_hiatus';
	session_hiatus_num: number;
	tab_hidden: boolean;
}

interface SessionResume extends CoreParams {
	event: 'zt_session_resume';
	hiatus_s: number;
	session_resume_num?: number;
}

interface SessionEnd extends CoreParams {
	// Added session length and/or engadgement time?
	event: 'zt_session_end';
}

interface SessionEndReplay extends CoreParams {
	event: 'zt_session_end_replay';
	return_delay_s: number; //seconds
	return_type: 'reload' | 'quick_nav' | 'return';
}

interface SBMInteraction extends CoreParams {
	event: 'sbm_interaction' | string;
	context: SBMContext;
	upload_method?: 'dialog' | 'drag_drop';
}

interface CropperInteraction extends CoreParams, SBMInteraction {
	event: 'crop_interaction';
	dx?: number;
	dy?: number;
	dw?: number;
	dh?: number;
	zoom_method?: 'button' | 'wheel/pinch';
	zoom_percent?: number;
	d_zoom_percent?: number;
}

export interface ScenarioParams {
	scenario_id: string;
	scenario_num: number;
	scenario_blinds: ScenarioBlindTypes;
	scenario_bg_type: BgTypes;
}

interface ScenarioStart extends CoreParams, SBMInteraction, ScenarioParams {
	event: 'scenario_start';
}

interface ScenarioUpdate extends CoreParams, SBMInteraction, ScenarioParams {
	event: 'scenario_update';
}

interface DetectionError extends CoreParams, ScenarioParams {
	event: 'detection_error';
	elapsed_s: number;
	indoorUUID: string;
	outdoorUUID: string;
	timed_out_task?: 'indoor' | 'outdoor' | 'both';
}

interface DetectionServiceError extends CoreParams, ScenarioParams {
	event: 'detection_service_error';
	error: string;
	full_error: Error;
	elapsed_s: number;
}

interface DetectionAccessActivationError extends CoreParams {
	event: 'detection_service_aa_error';
	error: string;
	full_error: Error;
}

interface OpeningsDetected extends CoreParams, ScenarioParams {
	event: 'detection_openings_detected';
	elapsed_s: number;
	indoorUUID: string;
	outdoorUUID: string;
}

interface ErrorModalInteraction extends CoreParams, ScenarioParams {
	// Currently referring to the detection error modal. Can probably be used for the other error modal too, or if not will need to rename and refactor.
	event: 'error_modal_interaction';
	modal_type: string;
	close_method?: DialogCloseSource;
}

interface SelectOpeningScreenInteraction extends CoreParams, ScenarioParams {
	event: 'sos_interaction';
	skip_type?: 'waiting' | 'banner';
}

interface QuizModalClose extends CoreParams, ScenarioParams {
	event: 'quiz_modal_close';
	quiz_type: 'dyb' | 'detection' | 'iv'; // Really hate the use of 'dyb' here as DYB now refers to the entire app. Should be called '3DC' mode or 'no background' mode or something but would require a lot of refactoring to change now as it is everywhere in the codebase.
	slides_viewed: number;
	viewed_to_end: boolean; // Maybe find better name
	close_method: QuizCloseMethod;
}

/* -------- Union of all event interfaces -------- */
type GtmEvent =
	| SessionStart
	| SessionReturn
	| SessionHiatus
	| SessionResume
	| SessionEnd
	| SessionEndReplay
	| SBMInteraction
	| CropperInteraction
	| ScenarioStart
	| ScenarioUpdate
	| DetectionError
	| DetectionServiceError
	| DetectionAccessActivationError
	| OpeningsDetected
	| ErrorModalInteraction
	| SelectOpeningScreenInteraction
	| QuizModalClose;

// Re-maps the key of each member to its literal 'event' value
export type EventRegistry = {
	[E in GtmEvent as E['event']]: E;
};

/*
+===============================================================================+
| 						     	Event Keys						       	   		|
+-------------------------------------------------------------------------------+
|	Category/location -> Event / Event group -> Unique interaction event key	|
+===============================================================================+
*/

// zt prefix as GA4 already has some session events (e.g. session_start)
export const SESSION_EVENTS = {
	// All session events are Milestone events
	session_start: 'zt_session_start',
	session_return: 'zt_session_return',
	session_hiatus: 'zt_session_hiatus',
	session_resume: 'zt_session_resume',
	session_end: 'zt_session_end',
	session_end_replay: 'zt_session_end_replay',
} as const;

export const BACKGROUND_MODAL_EVENTS = {
	GENERIC_SBM: {
		outdoor_blinds: 'sbm_outdoor_blinds',
		interior_blinds: 'sbm_indoor_blinds',
		upload: 'sbm_use_own_photo',
		project_sample: 'sbm_sample_project',
		interior_sample_1: 'sbm_interior_sample_1',
		interior_sample_2: 'sbm_interior_sample_2',
		interior_sample_3: 'sbm_interior_sample_3',
		outdoor_sample_1: 'sbm_outdoor_sample_1',
		outdoor_sample_2: 'sbm_outdoor_sample_2',
		outdoor_sample_3: 'sbm_outdoor_sample_3',
		no_background: 'sbm_no_background',
		live_background: 'sbm_live_background',
		ar_view: 'sbm_ar_view',
		close: 'sbm_close',
		open: 'sbm_open',
		upload_photo: 'sbm_upload_photo',
		photo_uploaded: 'sbm_photo_uploaded',
		delete: 'sbm_delete_photo',
		upload_new: 'sbm_upload_new_photo',
	},
	CROPPER: {
		// Generic cropper events
		rotate: 'cropper_rotate',
		zoom_in: 'cropper_zoom_in',
		zoom_out: 'cropper_zoom_out',
		reset: 'cropper_reset',

		crop_top_left: 'cropper_crop_top_left',
		crop_top: 'cropper_crop_top',
		crop_top_right: 'cropper_crop_top_right',
		crop_right: 'cropper_crop_right',
		crop_bottom_right: 'cropper_crop_bottom_right',
		crop_bottom: 'cropper_crop_bottom',
		crop_bottom_left: 'cropper_crop_bottom_left',
		crop_left: 'cropper_crop_left',
		move: 'cropper_move',
	},
	scenario_start: 'scenario_start', // Milestone event for scenario // Should keep here or make a seperate group for scenario events?
	SCENARIO_UPDATE: {
		edit_image: 'scenario_update_edit_image',
		add_blind_type: 'scenario_update_add_blind_type',
	},
} as const;

export const DETECTION_EVENTS = {
	D_ERRORS: {
		no_openings: 'detection_error_no_openings',
		one_task_timeout: 'detection_error_one_task_timeout',
		both_task_timeout: 'detection_error_both_tasks_timeout',
	},
	A_ERRORS: {
		access_error: 'detection_service_access_error',
		activation_error: 'detection_service_activation_error',
	},
	detection_service_error: 'detection_service_error', // For errors in the detection service that aren't necessarily detection errors (e.g. failure to upload image, failure to submit tasks etc.)
	openings_detected: 'detection_openings_detected',
} as const;

export const ERROR_MODAL_EVENTS = {
	GENERIC_EM: {
		close: 'error_modal_close',
	},
} as const;

export const SELECT_OPENING_SCREEN_EVENTS = {
	GENERIC_SOS: {
		skip: 'sos_skip',
	},
} as const;

export const QUIZ_MODAL_EVENTS = {
	close: 'quiz_modal_close',
} as const;

export const EVENT_KEY_MAP = buildKeyToEvent(
	{
		SESSION_EVENTS,
		BACKGROUND_MODAL_EVENTS,
		DETECTION_EVENTS,
		ERROR_MODAL_EVENTS,
		SELECT_OPENING_SCREEN_EVENTS,
		QUIZ_MODAL_EVENTS,
	} as const,
	{
		CROPPER: 'crop_interaction',
		GENERIC_SBM: 'sbm_interaction',
		SCENARIO_UPDATE: 'scenario_update',
		D_ERRORS: 'detection_error',
		A_ERRORS: 'detection_service_aa_error',
		GENERIC_EM: 'error_modal_interaction',
		GENERIC_SOS: 'sos_interaction',
	} as const, // CROPPER maps to crop_interaction etc.
);

export const NON_SCENARIO_KEYS = [
	...collectLeafStrings(SESSION_EVENTS),
	...collectLeafStrings(BACKGROUND_MODAL_EVENTS.GENERIC_SBM),
	...collectLeafStrings(BACKGROUND_MODAL_EVENTS.CROPPER),
	...collectLeafStrings(DETECTION_EVENTS.A_ERRORS),
] as const satisfies readonly AnyKey[];
